import type { ControlA11y } from '../../guidance/GuidedField';
import { GroupedCombobox, type ComboLoadResult, type ComboOption } from './GroupedCombobox';

/**
 * Several values chosen from a reference list: chips for what is chosen (each with a remove button) and a combobox to add more, with the same
 * search, groups, loading / empty / error states and custom entry as GroupedCombobox. Values that are already chosen are left out of the list.
 *
 * A value that matches no option (an old free-text value) stays as a chip marked "custom, kept as entered": it is never dropped or rewritten.
 */

export interface MultiSelectComboboxProps {
  a11y: ControlA11y;
  /** The chosen values, in order. */
  values: string[];
  onChange: (values: string[]) => void;
  load: (query: string, signal: AbortSignal) => Promise<ComboLoadResult>;
  allowCustom?: boolean;
  confirmCustom?: boolean;
  onCustom?: (name: string) => Promise<ComboOption>;
  /** `true`: a standard or company option; `false`: kept as entered; `undefined`: still being checked (no marker yet). */
  isKnown?: (value: string) => boolean | undefined;
  /** The thing chosen, for the chips' list name and the remove buttons: "skill", "language". */
  noun: string;
  placeholder?: string;
  disabled?: boolean;
  maxItems?: number | undefined;
}

export function MultiSelectCombobox({ a11y, values, onChange, load, allowCustom, confirmCustom, onCustom, isKnown, noun, placeholder, disabled, maxItems }: MultiSelectComboboxProps) {
  const chosen = new Set(values.map((v) => v.toLowerCase()));
  const full = maxItems !== undefined && values.length >= maxItems;

  const add = (name: string) => {
    if (!name.trim() || chosen.has(name.toLowerCase())) return;
    onChange([...values, name]);
  };

  return (
    <div className="jr-multi">
      {values.length > 0 && (
        <ul className="jr-chips" aria-label={`Chosen ${noun} values`}>
          {values.map((v) => {
            const known = isKnown?.(v);
            return (
              <li key={v} className={known === false ? 'jr-chip jr-chip--custom' : 'jr-chip'}>
                <span>{v}</span>
                {known === false && <span className="jr-chip__tag"> custom<span className="jr-sr-only">, kept as entered</span></span>}
                <button type="button" className="jr-chip__remove" disabled={disabled} onClick={() => onChange(values.filter((x) => x !== v))}>
                  <span aria-hidden="true">×</span>
                  <span className="jr-sr-only">Remove {v}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <GroupedCombobox
        a11y={a11y}
        mode="select"
        value=""
        valueLabel=""
        disabled={disabled || full}
        placeholder={full ? `You have added the most ${noun} values (${maxItems}).` : placeholder ?? `Search or add a ${noun}`}
        allowCustom={allowCustom ?? false}
        confirmCustom={confirmCustom ?? false}
        {...(onCustom ? { onCustom } : {})}
        load={async (query, signal) => {
          const result = await load(query, signal);
          return {
            ...result,
            groups: result.groups
              .map((g) => ({ ...g, options: g.options.filter((o) => o.actionId || !chosen.has(o.label.toLowerCase())) }))
              .filter((g) => g.options.length > 0),
          };
        }}
        onSelect={(option) => add(option.label)}
        emptyText="No more matches. Try a different word."
      />
    </div>
  );
}
